// Markup for the redesigned event popup shown under the graph (the tray below
// chartArea.bottom). Figma "Events on graph improvements" 12009:11615.
//
// Three rows separated by hairlines:
//   1. event icon + amount, pressure reading, and the red pressure-alert line
//   2. clock icon + start / end timestamps
//   3. source icon + sensor name
//
// React-free and icon-agnostic: every image src is passed in by EventPopUp.jsx
// (which owns the webpack `require('images/...')` calls), so this module can be
// unit-tested with plain `node --test`.

const {
  buildGraphPressureRows,
  POPUP_TEXT_STYLE,
} = require('./eventPopUpHelpers');

// Every row reserves the same 24px icon slot so all three rows' text starts on
// one vertical line, even where the glyph itself is smaller (the piezometer is
// drawn at 16px inside its slot).
const ICON_SLOT = 24;
const SOURCE_ICON_SIZE = 16;
const ROW_STYLE = 'display: flex;gap: 8px;align-items: center';
const CARD_STYLE =
  'display: flex;flex-direction: column;gap: 8px;padding: 4px 0';
const SEPARATOR =
  "<div style='height: 1px;background: var(--color-grey-91)'></div>";
// Rows without an icon indent their text by the slot width plus the row gap.
const ICONLESS_INDENT = 'padding-left: ' + (ICON_SLOT + 8) + 'px';
const TIME_COLUMN_STYLE =
  'display: flex;flex-direction: column;line-height: 1.2;color: var(--color-grey-71)';

// Grouped popup: one block per alert, each behind a grey ordinal, separated by
// a full-width hairline with more breathing room than the within-event rows so
// the two levels of division stay distinguishable.
const GROUP_CARD_STYLE =
  'display: flex;flex-direction: column;gap: 16px;padding: 4px 0';
const GROUP_BLOCK_STYLE = 'display: flex;gap: 8px;align-items: flex-start';
const GROUP_SEPARATOR =
  "<div style='height: 1px;background: var(--color-grey-91)'></div>";
// Nudged down to sit level with the first row's text, which is centred against
// its 24px icon.
const GROUP_INDEX_STYLE =
  'flex: 0 0 auto;min-width: 12px;padding-top: 3px;font-size: 14px;' +
  'line-height: 1.2;color: var(--color-grey-71)';

// The caret only ever appears on the popup opened from the pressure-alert
// badge. Two stacked triangles: the border-coloured one behind, a white one
// nudged up over it, leaving only the card's 1px outline along the slopes.
const ARROW_HEIGHT = 6;
const ARROW_HALF_WIDTH = 6;

// A glyph centred in the shared 24px slot. `size` defaults to filling the slot.
const iconTag = (src, size) => {
  const glyph = size || ICON_SLOT;
  return (
    "<div style='display: flex;align-items: center;justify-content: center;" +
    'flex: 0 0 auto;width: ' +
    ICON_SLOT +
    'px;height: ' +
    ICON_SLOT +
    "px'>" +
    "<img style='width: " +
    glyph +
    'px;height: ' +
    glyph +
    "px' src='" +
    src +
    "'/>" +
    '</div>'
  );
};

// Sits outside the card's bottom edge, pointing down at the pressure-alert
// badge the popup was opened from.
const buildArrow = () => {
  const triangle = (color, offsetY) =>
    "<div style='position: absolute;left: 0;top: " +
    offsetY +
    'px;width: 0;height: 0;' +
    'border-left: ' +
    ARROW_HALF_WIDTH +
    'px solid transparent;border-right: ' +
    ARROW_HALF_WIDTH +
    'px solid transparent;' +
    'border-top: ' +
    ARROW_HEIGHT +
    'px solid ' +
    color +
    "'></div>";
  // Centred on the card, which is itself centred on the badge — so the caret
  // lands on the badge's centre without either having to know the other's
  // geometry.
  return (
    "<div style='position: absolute;bottom: -" +
    (ARROW_HEIGHT + 1) +
    'px;left: 50%;transform: translateX(-50%);width: ' +
    ARROW_HALF_WIDTH * 2 +
    'px;height: ' +
    ARROW_HEIGHT +
    "px'>" +
    triangle('var(--color-grey-91)', 0) +
    triangle('var(--color-grey-100)', -1.5) +
    '</div>'
  );
};

const semiBoldText = (text, extraStyle) =>
  "<div class='font-weight-semi-bold' style='" +
  POPUP_TEXT_STYLE +
  (extraStyle ? ';' + extraStyle : '') +
  "'>" +
  text +
  '</div>';

// Row 1 — the measurement block. `extraText` carries the event type's own
// second line (fertilization's "CONTAINING x kg/ha"); `deleteRow` is the
// existing trash-icon markup built by EventPopUp, pushed to the far right so
// the card matches the design when the user may not delete (it is `d-none`).
const buildMeasurementRow = (options) => {
  const pressureRows = buildGraphPressureRows({
    pressure: options.pressure,
    pressureUnit: options.pressureUnit,
    pressureLabel: options.pressureLabel,
    alertLevel: options.pressureAlert,
    alertIconSrc: options.alertIconSrc,
    t: options.t,
  });
  return (
    "<div style='" +
    ROW_STYLE +
    "'>" +
    iconTag(options.eventIconSrc) +
    "<div style='display: flex;flex-direction: column;justify-content: center'>" +
    semiBoldText(options.amountText) +
    (options.extraText ? semiBoldText(options.extraText) : '') +
    pressureRows +
    '</div>' +
    "<div style='margin-left: auto'>" +
    (options.deleteRow || '') +
    '</div>' +
    '</div>'
  );
};

// A date-over-time stack: "9 JUN" above "8:30 am".
const buildTimeColumn = (dateStr, timeStr) =>
  "<div style='" +
  TIME_COLUMN_STYLE +
  "'>" +
  "<div style='font-size: 12px'>" +
  dateStr +
  '</div>' +
  "<div class='font-weight-semi-bold' style='font-size: 14px'>" +
  timeStr +
  '</div>' +
  '</div>';

// Row 2 — the time frame. Fertilization events on the graph have no end date,
// so the dash and end column are dropped rather than rendered empty.
const buildTimeFrameRow = (options) => {
  const endBlock = options.hideEndDate
    ? ''
    : "<div style='" +
      POPUP_TEXT_STYLE +
      "'>-</div>" +
      buildTimeColumn(options.endDateStr, options.endTimeStr);
  return (
    "<div style='" +
    ROW_STYLE +
    "'>" +
    iconTag(options.timerIconSrc) +
    buildTimeColumn(options.begDateStr, options.begTimeStr) +
    endBlock +
    '</div>'
  );
};

// Row 3 — where the reading came from. Only sensor-sourced events carry an
// icon; manual entries and rain gauges show the name alone, indented to match.
// Omitted entirely when the event has no source to name.
const buildSourceRow = (options) => {
  if (!options.sourceText) {
    return '';
  }
  const body = options.sourceIconSrc
    ? iconTag(options.sourceIconSrc, SOURCE_ICON_SIZE) +
      semiBoldText(options.sourceText)
    : semiBoldText(options.sourceText, ICONLESS_INDENT);
  return "<div style='" + ROW_STYLE + "'>" + body + '</div>';
};

// One event's rows. Separators are emitted only between rows that exist, so a
// sourceless event does not end on a dangling hairline.
const buildCardBody = (options) => {
  const rows = [buildMeasurementRow(options), buildTimeFrameRow(options)];
  const sourceRow = buildSourceRow(options);
  if (sourceRow) {
    rows.push(sourceRow);
  }
  return rows.join(SEPARATOR);
};

// Assembles the card. `bodyOnly` returns just the rows, so the grouped
// pressure-alert popup can stack several events built by the very same code
// path. The caret is added only for the badge-anchored popup (`showArrow`); it
// is a sibling of the rows, positioned against the popup itself
// (.dropdown-menu is already absolutely positioned, so it is the caret's
// containing block).
const buildGraphEventCard = (options) => {
  const body = buildCardBody(options);
  if (options.bodyOnly) {
    return body;
  }
  return (
    "<div style='" +
    CARD_STYLE +
    "'>" +
    body +
    '</div>' +
    (options.showArrow ? buildArrow() : '')
  );
};

// Several alerts from the same day share one badge, so their popup stacks each
// event's rows behind a grey ordinal. Bodies come from buildGraphEventCard with
// `bodyOnly`, so a grouped entry renders exactly like a lone one.
const buildGraphEventCardGroup = (bodies, options) => {
  const settings = options || {};
  const showIndex = settings.showIndex && bodies.length > 1;
  const blocks = bodies.map((body, i) => {
    const index = showIndex
      ? "<div style='" + GROUP_INDEX_STYLE + "'>" + (i + 1) + '</div>'
      : '';
    return (
      "<div style='" +
      GROUP_BLOCK_STYLE +
      "'>" +
      index +
      "<div style='" +
      CARD_STYLE +
      ";flex: 1 1 auto'>" +
      body +
      '</div>' +
      '</div>'
    );
  });
  return (
    "<div style='" +
    GROUP_CARD_STYLE +
    "'>" +
    blocks.join(GROUP_SEPARATOR) +
    '</div>' +
    (settings.showArrow ? buildArrow() : '')
  );
};

module.exports = {
  buildGraphEventCard,
  buildGraphEventCardGroup,
  buildCardBody,
  buildMeasurementRow,
  buildTimeFrameRow,
  buildSourceRow,
  buildTimeColumn,
  buildArrow,
  SEPARATOR,
  GROUP_SEPARATOR,
  ICONLESS_INDENT,
  ICON_SLOT,
  SOURCE_ICON_SIZE,
  ARROW_HEIGHT,
  ARROW_HALF_WIDTH,
};
